<template>
  <div
    class="app-switch"
    :class="[
      `app-switch--${size}`,
      {
        'app-switch--light': lightMode,
        'app-switch--label-left': labelLeft,
        'app-switch--disabled': disabled,
        'app-switch--on': value,
        'app-switch--square': square,
      },
    ]"
  >
    <v-switch
      class="app-switch__ctl"
      :input-value="value"
      :disabled="disabled"
      :loading="loading ? 'primary' : false"
      :dense="size !== 'md'"
      color="primary"
      inset
      :ripple="false"
      hide-details
      @change="$emit('input', !!$event)"
    ></v-switch>
    <span v-if="$slots.default" class="app-switch__label" @click="toggle"><slot></slot></span>
  </div>
</template>

<script>
import Themeable from "vuetify/lib/mixins/themeable";

// v-switch 래퍼 — 지오메트리·색상 모두 순정(Material, color=primary, 테마 자동). 라벨/사이즈 토큰/label-left만 추가.
//  v-model(Boolean), 라벨=기본 슬롯(클릭 토글), label-left, size 토큰(xs/sm/md), loading(썸 스피너 — v-switch 상속).
//  테마: Themeable 자동 상속, dark/light prop은 오버라이드.
export default {
  name: "AppSwitch",
  mixins: [Themeable],
  props: {
    value: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    // 라벨을 스위치 왼쪽에 배치
    labelLeft: { type: Boolean, default: false },
    // 각진 사각형 variant (트랙/썸 모서리를 각지게)
    square: { type: Boolean, default: false },
    // 사이즈 토큰(순정 매핑): md=기본형, sm/xs=dense (xs는 라벨만 축소 — v-switch 순정엔 2단뿐)
    size: {
      type: String,
      default: "sm",
      validator: (v) => ["xs", "sm", "md"].includes(v),
    },
  },
  computed: {
    lightMode() {
      return !this.isDark;
    },
  },
  methods: {
    toggle() {
      if (this.disabled || this.loading) return;
      this.$emit("input", !this.value);
    },
  },
};
</script>

<style scoped>
/* 래핑 원칙: 지오메트리(트랙/썸 크기·위치·리플)는 Vuetify 순정 유지, "색상만" App* 톤으로 교체.
   사이즈 토큰은 scale()로 순정 형태를 그대로 축소 (내부 치수 오버라이드 없음). */
.app-switch {
  --sw-font: 12px;
  display: inline-flex;
  align-items: center;
}
.app-switch--label-left {
  flex-direction: row-reverse; /* 라벨 → 스위치 순서 반전 */
}

/* 레이아웃 하이진 — 간격은 라벨 쪽(아래 __label 마진)에만, 스위치 바깥쪽은 벌리지 않음 */
.app-switch__ctl {
  margin: 0 !important;
  padding: 0 !important;
  flex: 0 0 auto;
}
.app-switch__ctl ::v-deep .v-input__slot {
  margin: 0 !important;
}
.app-switch__ctl ::v-deep .v-messages {
  display: none;
}

/* ── 사이즈 토큰: 순정 dense/기본형 매핑 (scale 미사용) ── */
.app-switch--xs {
  --sw-font: 11px;
}
.app-switch--md {
  --sw-font: 13px;
}

/* (트랙/썸 색 오버라이드 제거 — v-switch 순정 Material 색·테마 자동을 그대로 사용) */
/* 예외: 다크에서 ON 트랙(기본 32% 틴트)이 어두운 배경에 묻힘 → 다크에서만 불투명도 상향 */
.app-switch:not(.app-switch--light) .app-switch__ctl.v-input--is-dirty ::v-deep .v-input--switch__track {
  opacity: 0.6 !important;
}
/* 그로 인해 primary 썸이 트랙과 동색으로 묻힘 → 다크 ON 썸만 밝은 보라로 (썸은 currentColor로 칠해짐) */
.app-switch:not(.app-switch--light) .app-switch__ctl.v-input--is-dirty ::v-deep .v-input--switch__thumb {
  color: #cbb8ff !important;
}

/* 라벨 — 간격은 스위치와 마주보는 면에만 (inset 트랙 오버행 4px 감안해 8px = 시각 ~4px) */
.app-switch__label {
  margin-left: 8px;
  font-size: var(--sw-font);
  color: #9a93ad;
  cursor: pointer;
  user-select: none;
  transition: color 0.18s ease;
}
.app-switch--on .app-switch__label {
  color: #dcdce6;
}

/* label-left: 라벨이 왼쪽 → 간격도 오른쪽 면으로 */
.app-switch--label-left .app-switch__label {
  margin-left: 0;
  margin-right: 8px;
}

/* 비활성 */
.app-switch--disabled {
  opacity: 0.45;
}
.app-switch--disabled .app-switch__label {
  cursor: default;
}

/* ── square variant: inset 트랙/썸 모서리 각지게 (지오메트리는 순정 유지) ── */
.app-switch--square .app-switch__ctl ::v-deep .v-input--switch__track {
  border-radius: 3px !important;
}
.app-switch--square .app-switch__ctl ::v-deep .v-input--switch__thumb {
  border-radius: 2px !important;
}

/* ── light theme (라벨 색만) ── */
.app-switch--light .app-switch__label {
  color: #8a84a0;
}
.app-switch--light.app-switch--on .app-switch__label {
  color: #37324a;
}
</style>
